import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { AlertStack } from "../../components/Alert/AlertStack";
import { Button } from "../../components/Button/Button";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Switch } from "../../components/Switch/Switch";
import { Tabs } from "../../components/Tabs/Tabs";
import { useToast } from "../../components/Toast/toastContext";
import { getBotsOverview, runBotTick, botError } from "./api";
import { settingsLink } from "./links";
import { OverviewTab } from "./OverviewTab";
import { SettingsTab } from "./SettingsTab";
import { SourceTab } from "./SourceTab";
import { GuildTemplatesTab } from "./GuildTemplatesTab";
import { PoolTab } from "./PoolTab";
import { PhrasesTab } from "./PhrasesTab";
import { ActivityTab } from "./ActivityTab";
import { useBotSettingsDraft } from "./useBotSettingsDraft";
import styles from "./BotsPage.module.css";

/**
 * Left to right: what is happening, then the four tabs a project is set up
 * through in the order it is set up, then the two that are read once it is.
 * The inline `tabs={[…]}` literal below carries the labels for the same list
 * and has to move with it.
 */
const TAB_IDS = [
  "overview",
  "settings",
  "source",
  "guilds",
  "phrases",
  "pool",
  "activity",
] as const;
type TabID = (typeof TAB_IDS)[number];

/**
 * `/projects/:id/bots` — everything about this project's bots.
 *
 * It answers three questions and offers two levers — is the system alive, how
 * much of the project is us, what is it doing right now, plus the master switch
 * and a manual sweep — and, since the `bots` section left the Config page, it
 * also holds the balance itself on the Settings tab.
 *
 * Mockup: bots-page.html (Overview / Pool / Activity), bots-phrases.html, and
 * t-103/bots-page-shell.html for the seven-tab row.
 */
export function BotsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const { push } = useToast();
  const [ticking, setTicking] = useState(false);

  const rawTab = params.get("tab") ?? "overview";
  const tab: TabID = (TAB_IDS as readonly string[]).includes(rawTab)
    ? (rawTab as TabID)
    : "overview";

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["project", id, "bots", "overview"],
    queryFn: () => getBotsOverview(id),
    enabled: !!id,
    // The page is a live view of a worker, so it refreshes on its own — but
    // slowly: nothing here changes faster than a bot's own cooldown.
    refetchInterval: 30_000,
  });

  /**
   * ONE draft of the bot settings for the whole page, held HERE.
   *
   * Settings and Source are two forms over one document — the hook that runs it
   * says so in as many words — and both of those tabs are mounted
   * conditionally. A draft that lived inside either of them was destroyed by a
   * click on any other tab, silently, while the same draft went on installing
   * a beforeunload guard for the browser's own navigation. Before the section
   * moved onto this page the form was a route of its own and the case could
   * not arise; owning the draft above the tab row is what puts it back.
   */
  const draft = useBotSettingsDraft(id);

  // The master switch writes ONE key of the bot settings, as a merge patch of
  // one word. It used to read the whole project config back, lay `enabled` over
  // the browser's copy of it and send all of it — so a switch pressed on a
  // stale tab could carry twenty other sections back to what they were before
  // somebody else edited them. Nothing about that risk was worth the switch.
  const toggle = useMutation({
    mutationFn: (next: boolean) => draft.write({ enabled: next }),
    onSuccess: () => {
      void refetch();
    },
    onError: (err) => push({ variant: "danger", message: botError(err) }),
  });

  const overview = data?.overview;
  const enabled = overview?.enabled ?? false;
  // `!isError` as well: «Simulation is off — bots are frozen» is a statement
  // about the project, and a request that never answered has not made it. The
  // banner below says what actually happened instead.
  const showFrozen = !enabled && !isLoading && !isError;
  const showProblems = !!overview && overview.problems.length > 0;

  async function onRunTick() {
    setTicking(true);
    try {
      await runBotTick(id);
      await refetch();
      push({ variant: "success", message: "Sweep finished." });
    } catch (err) {
      push({ variant: "danger", message: botError(err) });
    } finally {
      setTicking(false);
    }
  }

  const header = (
    <PageHeader
      breadcrumb={[
        { label: "Projects", to: "/projects" },
        { label: "Bots" },
      ]}
      title="Bots"
      actions={
        <>
          <Switch
            checked={enabled}
            onChange={(next) => toggle.mutate(next)}
            // Gated on the SETTINGS as well as on the overview: the switch
            // writes the settings document, so a click before it has arrived
            // has nothing to patch — and would look, on screen, like a click
            // that did nothing at all.
            // …and off entirely when the settings could not be read: `write`
            // would throw a plain Error, which `botError` can only render as
            // «something went wrong». Both tabs name the failure; the header
            // has nowhere to put a sentence, so it declines the click instead.
            disabled={
              toggle.isPending || isLoading || draft.loading || !!draft.loadError
            }
            label={enabled ? "Simulation on" : "Simulation off"}
          />
          <Button
            variant="secondary"
            icon={<RefreshCw size={14} />}
            onClick={onRunTick}
            loading={ticking}
            disabled={!enabled}
          >
            Run tick now
          </Button>
        </>
      }
      tabs={
        <Tabs
          activeId={tab}
          onChange={(next) => {
            // `replace` on the tab row and a push on every in-page LINK: the
            // row is a view control (Back should leave the page, not walk back
            // through seven tabs), while a link the operator followed to reach
            // a field is a step Back has to be able to undo. React Router's
            // <Link> pushes by default, which is why the links say nothing.
            const p = new URLSearchParams(params);
            p.set("tab", next);
            setParams(p, { replace: true });
          }}
          tabs={[
            { id: "overview", label: "Overview" },
            { id: "settings", label: "Settings" },
            { id: "source", label: "Source" },
            { id: "guilds", label: "Guild templates" },
            { id: "phrases", label: "Phrases" },
            { id: "pool", label: "Pool" },
            { id: "activity", label: "Activity" },
          ]}
        />
      }
    />
  );

  return (
    <>
      {header}
      <div className={styles.page}>
        <AlertStack className={styles.alerts}>
          {isError && (
            <Alert
              variant="danger"
              title="Couldn't read what the simulation is doing"
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<RefreshCw size={13} />}
                  onClick={() => void refetch()}
                >
                  Try again
                </Button>
              }
            >
              The bots themselves are unaffected — this page could not fetch the
              overview, so every number on it, the master switch included, is
              unknown rather than zero.
            </Alert>
          )}
          {showFrozen && (
            <Alert variant="warn" title="Simulation is off — bots are frozen">
              They stay in their guilds and keep every message they have already
              sent. Nothing new is posted, no help is answered, no one joins or
              leaves. Turning it back on resumes from where it stopped.
            </Alert>
          )}
          {showProblems && (
            <Alert
              variant="danger"
              title="Configuration is invalid — the simulation is not running"
            >
              {overview?.problems.map((p) => (
                <div key={p.path}>
                  <code>{p.path}</code>: {p.message}
                </div>
              ))}
              <Link to={settingsLink(id)}>Open Settings →</Link>
            </Alert>
          )}
        </AlertStack>

        {tab === "overview" && (
          <OverviewTab
            projectID={id}
            data={data}
            loading={isLoading}
            enabled={enabled}
          />
        )}
        {tab === "settings" && <SettingsTab projectID={id} draft={draft} />}
        {tab === "source" && <SourceTab projectID={id} draft={draft} />}
        {tab === "guilds" && <GuildTemplatesTab projectID={id} />}
        {tab === "phrases" && <PhrasesTab projectID={id} />}
        {tab === "pool" && <PoolTab projectID={id} />}
        {tab === "activity" && <ActivityTab projectID={id} />}
      </div>
    </>
  );
}
